import { ReactNode, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import {
  LayoutMode_Enum,
  LayoutType_Enum,
  ListDevice,
} from '@weave/schema-gen-ts/dist/schemas/phone/devices/v2/devices.pb';
import { LineKeysTypes } from '@frontend/api-line-keys';
import { useTranslation } from '@frontend/i18n';
import { useNavSize } from '@frontend/responsiveness';
import { Button, CheckboxField, Heading, Text, useFormField } from '@frontend/design-system';
import { DeviceLayout } from '../device-layout/device-layout';
import { isHandheldLayout } from '../utils';
import { EmptyStateLayout } from './empty-state';
import { LineKeyPagination } from './pagination';
import { TouchScreenModeToggle } from './touch-screen-mode-toggle';

type Props = {
  device: LineKeysTypes.ListDeviceType | undefined;
  model: LineKeysTypes.ModelInfoType | undefined;
  lineKeys: LineKeysTypes.GetLineKeysType['output']['lineKeys'] | undefined;
  isPreviewEnabled?: boolean;
  setShowDeviceLayoutPreview?: (val: boolean) => void;
  showFirstPageOnHomeScreen?: boolean;
  onShowFirstPageOnHomeScreenChange?: (value: boolean) => void;
};

export const Layout = ({
  device,
  model,
  lineKeys,
  setShowDeviceLayoutPreview,
  isPreviewEnabled,
  showFirstPageOnHomeScreen,
  onShowFirstPageOnHomeScreenChange,
}: Props) => {
  const { t } = useTranslation('phone', { keyPrefix: 'line-keys' });
  const paginationRef = useRef<HTMLDivElement>(null);
  const touchScreenRef = useRef<HTMLDivElement>(null);
  const [pageNum, setPageNum] = useState(1);
  const [hasHorizontalScrollbar, setHasHorizontalScrollbar] = useState(false);
  const [touchScreenMode, setTouchScreenMode] = useState(LayoutMode_Enum.COLLAPSED);
  const isHandheldDeviceLayout = isHandheldLayout(model);
  const handheldModel = model as
    | (LineKeysTypes.ModelInfoType & {
        handheldKeys?: { supportsHomeScreenFirstPageKeys?: boolean };
      })
    | undefined;
  const supportsHomeScreenFirstPageKeys = !!handheldModel?.handheldKeys?.supportsHomeScreenFirstPageKeys;

  const selectProps = useFormField(
    {
      type: 'checkbox',
      value: !!showFirstPageOnHomeScreen,
    },
    [showFirstPageOnHomeScreen]
  );

  const handleShowFirstPageCheckboxChange = () => {
    onShowFirstPageOnHomeScreenChange?.(!showFirstPageOnHomeScreen);
  };

  const showFirstPageCheckboxProps = {
    ...selectProps,
    id: 'show-home-screen-first-page-keys',
    name: 'show-home-screen-first-page-keys',
    label: '',
    labelPlacement: 'right' as const,
    onChange: handleShowFirstPageCheckboxChange,
  };

  const isTouchScreenLayout = model?.layoutType === LayoutType_Enum.TOUCH_SCREEN;
  const usedLineKeysLength = lineKeys?.length ?? 0;

  const { isPaginationEnabled, totalPagesToShow } = isHandheldDeviceLayout
    ? getHandheldPaginationMeta(usedLineKeysLength)
    : isTouchScreenLayout
    ? getTouchKeysPaginationMeta({
        model,
        usedLineKeysLength,
        mode: touchScreenMode,
      })
    : getSideKeysPaginationMeta(model, usedLineKeysLength);

  useEffect(() => {
    const divElement = touchScreenRef.current;

    if (divElement) {
      const hasScrollbar = divElement.scrollWidth > divElement.clientWidth;
      setHasHorizontalScrollbar(hasScrollbar);
    }
  }, []);

  // Keep page number valid when total pages shrink after key removals.
  useEffect(() => {
    const safeTotalPages = Math.max(totalPagesToShow || 1, 1);

    setPageNum((prevPageNum) => {
      if (prevPageNum > safeTotalPages) return safeTotalPages;
      if (prevPageNum < 1) return 1;
      return prevPageNum;
    });
  }, [totalPagesToShow]);

  const handleToggleTouchScreenMode = () => {
    setTouchScreenMode((prev) =>
      prev === LayoutMode_Enum.COLLAPSED ? LayoutMode_Enum.EXPANDED : LayoutMode_Enum.COLLAPSED
    );
  };

  // some models can have line keys assigned to it but no model or model info to spec out the layout
  const isEmptyState = !model;
  return (
    <>
      {isEmptyState ? (
        <EmptyStateLayout setShowDeviceLayoutPreview={setShowDeviceLayoutPreview} isPreviewEnabled={isPreviewEnabled} />
      ) : (
        <Container isPreviewEnabled={isPreviewEnabled}>
          <ContainerHeader
            setShowDeviceLayoutPreview={setShowDeviceLayoutPreview}
            isPreviewEnabled={isPreviewEnabled}
            device={device}
          />
          <DeviceLayout model={model} isPreviewEnabled={isPreviewEnabled}>
            <DeviceLayout.Header model={model} device={device} />
            {isHandheldDeviceLayout ? (
              <DeviceLayout.Handheld
                model={model}
                lineKeys={lineKeys}
                isPreviewEnabled={isPreviewEnabled}
                pageNum={pageNum}
                ref={touchScreenRef}
              />
            ) : isTouchScreenLayout ? (
              <DeviceLayout.TouchScreen
                model={model}
                device={device}
                lineKeys={lineKeys}
                isPreviewEnabled={isPreviewEnabled}
                pageNum={pageNum}
                touchScreenMode={touchScreenMode}
                onToggleMode={handleToggleTouchScreenMode}
                ref={touchScreenRef}
              />
            ) : (
              <DeviceLayout.LayoutWithKeys
                model={model}
                device={device}
                isPreviewEnabled={isPreviewEnabled}
                isPaginationEnabled={isPaginationEnabled}
                pageNum={pageNum}
                lineKeys={lineKeys}
              />
            )}
            <DeviceLayout.Footer model={model} />
          </DeviceLayout>

          {(isPaginationEnabled || isHandheldDeviceLayout) && (
            <LineKeyPagination
              ref={paginationRef}
              setPageNum={setPageNum}
              totalPages={totalPagesToShow}
              pageNum={pageNum}
              helperText={
                model?.model === 't85w' || model?.model === 'ax83h' || model?.model === 'ax86r'
                  ? t('Click the arrows (< >) to edit each page of line keys')
                  : undefined
              }
            />
          )}
          {model.layoutType === LayoutType_Enum.TOUCH_SCREEN && (
            <TouchScreenModeToggle touchScreenMode={touchScreenMode} onToggleMode={handleToggleTouchScreenMode} />
          )}
          <Text css={{ color: 'var(--fabric-color-neutral-50)' }} size='medium'>
            {hasHorizontalScrollbar &&
              t(
                '*Horizontal scrolling is enabled due to your screen size. The actual device will show all line keys without scrolling.'
              )}{' '}
          </Text>
          {isHandheldDeviceLayout && supportsHomeScreenFirstPageKeys && (
            <div
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                margin-top: 24px;
              `}
            >
              <CheckboxField {...showFirstPageCheckboxProps} />
              <Text>{t("Show the first 4 line keys on the handset's home screen.")}</Text>
            </div>
          )}
        </Container>
      )}
    </>
  );
};

const Container = ({ children, isPreviewEnabled }: { isPreviewEnabled?: boolean; children: ReactNode }) => {
  return <section css={containerStyles({ isPreviewEnabled })}>{children}</section>;
};

type ContainerHeaderProps = {
  setShowDeviceLayoutPreview?: (val: boolean) => void;
  isPreviewEnabled?: boolean;
  device?: ListDevice;
};

const ContainerHeader = ({ setShowDeviceLayoutPreview, isPreviewEnabled, device }: ContainerHeaderProps) => {
  const navSize = useNavSize();
  const isMobile = navSize.isLte('medium');
  const { t } = useTranslation('phone', { keyPrefix: 'line-keys' });

  return (
    <>
      {isMobile && (
        <Button
          iconName='back'
          variant='secondary'
          size='large'
          onClick={() => setShowDeviceLayoutPreview?.(false)}
          css={css`
            border: 0;
          `}
        >
          {t('Back')}
        </Button>
      )}
      <Heading css={{ marginBottom: 'var(--fabric-space-gap-lg)' }} level={2}>
        {isPreviewEnabled
          ? t('{{name}} Layout', { name: device?.name })
          : t('Preview {{name}} Layout', { name: device?.name })}
      </Heading>
    </>
  );
};

const containerStyles = ({ isPreviewEnabled }: { isPreviewEnabled?: boolean }) => css`
  ${!isPreviewEnabled && `padding: 64px 24px 0;`}
  flex-grow: 1;
  overflow-y: auto;
`;

const getSideKeysPaginationMeta = (model?: LineKeysTypes.ModelInfoType, usedLineKeysLength = 0) => {
  if (!model) {
    return { keysPerPage: 0, totalPagesToShow: 0, isPaginationEnabled: false };
  }

  const { pages: maxPages = 1, keysPerPageLeft = 0, keysPerPageRight = 0 } = model?.sideKeys ?? {};

  const keysPerPage = keysPerPageLeft + keysPerPageRight;

  let paginationKeysToAdd;
  if (usedLineKeysLength - keysPerPage <= 0) {
    // On the first page only, no pagination keys are added unless we exceed the keysPerPage count
    paginationKeysToAdd = 0;
  } else {
    // Add one pagination key for each page
    paginationKeysToAdd = Math.ceil(usedLineKeysLength / keysPerPage);
  }

  const lineKeysPlusPaginationKeys = usedLineKeysLength + paginationKeysToAdd;
  const totalPagesToShow = keysPerPage > 0 ? Math.ceil(lineKeysPlusPaginationKeys / keysPerPage) || 1 : 1;

  const isPaginationEnabled = maxPages > 1 && totalPagesToShow > 1;

  return { keysPerPage, totalPagesToShow, isPaginationEnabled };
};

const getHandheldPaginationMeta = (usedLineKeysLength = 0) => {
  const maxLineKeys = 16;
  const itemsPerPage = 4;
  const cappedLineKeysLength = Math.min(usedLineKeysLength, maxLineKeys);
  const totalPagesToShow = cappedLineKeysLength > 0 ? Math.ceil(cappedLineKeysLength / itemsPerPage) : 1;
  const isPaginationEnabled = totalPagesToShow > 1;

  return { keysPerPage: itemsPerPage, totalPagesToShow, isPaginationEnabled };
};

const getTouchKeysPaginationMeta = ({
  model,
  usedLineKeysLength = 0,
  mode,
}: {
  model?: LineKeysTypes.ModelInfoType;
  usedLineKeysLength?: number;
  mode: LayoutMode_Enum;
}) => {
  if (!model) {
    return { keysPerPage: 0, totalPagesToShow: 0, isPaginationEnabled: false };
  }

  const { pages, columns = 0, rows = 0 } = model?.touchKeys ?? {};

  const keysPerPage = columns * rows;

  const expandedPages =
    pages?.reduce((acc, curr) => {
      return acc + (curr.mode === LayoutMode_Enum.EXPANDED ? 1 : 0);
    }, 0) ?? 0;

  const totalPagesToShow = keysPerPage > 0 ? Math.ceil(usedLineKeysLength / keysPerPage) || 1 : 1;

  const isPaginationEnabled = mode === LayoutMode_Enum.EXPANDED && expandedPages > 1 && totalPagesToShow > 1;

  return { keysPerPage, totalPagesToShow, isPaginationEnabled };
};
